import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Tooltip,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { useSpeechToText } from "../hooks/useSpeechToText";
import { format } from "date-fns";
import clinicalNoteService from "../services/clinicalNoteService";
import apiClient from "../services/apiClient";
import { formatToBackendDateTime } from "../utils/dateUtils";

// Define interface for EMR Medication to match ClinicalNotesPage usage
interface EMRMedication {
    id: string;
    medicationName: string;
    dosage: string;
    schedule: string;
    residentId: string;
    startDate?: string;
    endDate?: string;
    discontinued?: boolean;
}

interface DailyProgressNoteFormProps {
    residentId: string | null;
    onSuccess?: () => void;
    sx?: any;
}

const DailyProgressNoteForm: React.FC<DailyProgressNoteFormProps> = ({
    residentId,
    onSuccess,
    sx,
}) => {
    const [noteDateTime, setNoteDateTime] = useState<Date | null>(new Date());

    // Nurse Note Fields
    const [medicationName, setMedicationName] = useState("");
    const [dosage, setDosage] = useState("");
    const [schedule, setSchedule] = useState("");
    const [medicationStatus, setMedicationStatus] = useState("");
    const [result, setResult] = useState("");
    const [reason, setReason] = useState("");
    const [lowStock, setLowStock] = useState(false);

    // Dropdown data
    const [medications, setMedications] = useState<EMRMedication[]>([]);
    const [loadingMeds, setLoadingMeds] = useState(false);


    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [formErrors, setFormErrors] = useState<{
        date?: string;
        medication?: string;
        dosage?: string;
        schedule?: string;
        reason?: string;
        status?: string;
    }>({});

    const [existingNotes, setExistingNotes] = useState<any[]>([]);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [pendingSave, setPendingSave] = useState<boolean>(false);

    // Fetch active medications when residentId changes
    useEffect(() => {
        if (residentId) {
            fetchMedications(residentId);
            fetchExistingNotes(residentId);
        } else {
            setMedications([]);
            setExistingNotes([]);
        }
    }, [residentId, noteDateTime]);

    const fetchExistingNotes = async (id: string) => {
        try {
            const notes = await clinicalNoteService.getNurseNotesByResident(id);
            setExistingNotes(notes || []);
        } catch (error) {
            console.error("Failed to fetch existing notes:", error);
        }
    };

    const fetchMedications = async (id: string) => {
        try {
            setLoadingMeds(true);
            // Using EMR-medications endpoint as seen in ClinicalNotesPage
            let url = `/v1/EMR-medications?residentId=${id}`;

            // Fallback or verify if residentId param is supported, usually safer to filter client side if unsure,
            // but ClinicalNotesPage tries param first.

            let response;
            try {
                response = await apiClient.get(url);
            } catch (err) {
                console.warn("Failed to fetch with filter, trying all EMR medications");
                response = await apiClient.get("/v1/EMR-medications");
            }

            let apiMedications: EMRMedication[] = response.data.map((med: any) => ({
                id: med.id,
                medicationName: med.medicationName,
                dosage: med.dosage,
                schedule: med.schedule,
                residentId: med.residentId,
                startDate: med.startDate,
                endDate: med.endDate,
                discontinued: med.isDiscontinued || med.discontinued || false,
            }));

            // Filter by resident
            apiMedications = apiMedications.filter(m => String(m.residentId) === String(id));

            // Filter active (not discontinued) - assuming optional requirement but logical for dropdown
            // ClinicalNotesPage doesn't strictly filter discontinued in the list, but for specific administration it implies active.
            // Let's filter out discontinued to be safe/cleaner.
            apiMedications = apiMedications.filter(m => !m.discontinued);

            setMedications(apiMedications);
        } catch (e) {
            console.error("Failed to fetch medications", e);
        } finally {
            setLoadingMeds(false);
        }
    };


    // Auto-fill dosage/schedule when medication is selected from dropdown
    const handleMedicationChange = (selectedMedName: string) => {
        setMedicationName(selectedMedName);
        if (selectedMedName) {
            setFormErrors(prev => ({ ...prev, medication: undefined }));
        }

        const selectedMed = medications.find(m => m.medicationName === selectedMedName);
        if (selectedMed) {
            setDosage(selectedMed.dosage || "");
            if (selectedMed.dosage) setFormErrors(prev => ({ ...prev, dosage: undefined }));

            const finalSchedule = selectedMed.schedule || "";
            setSchedule(finalSchedule);
            if (finalSchedule) setFormErrors(prev => ({ ...prev, schedule: undefined }));
        }
    };

    const handleSave = async (isConfirmed: boolean = false) => {
        if (!residentId) {
            setError("Please select a resident first.");
            return;
        }

        const errors: any = {};
        if (!noteDateTime) errors.date = "Date & Time is required";
        if (!medicationStatus) errors.status = "Response Status is required";
        if (!medicationName) errors.medication = "Medication is required";
        if (!dosage) errors.dosage = "Dosage is required";
        if (!schedule) errors.schedule = "Schedule is required";

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        // ONE-TIME SCHEDULE VALIDATION
        // Check if "Once daily" and already given today
        // Only run this check if not already confirmed
        if (!isConfirmed && schedule.trim().toLowerCase() === "once daily") {
            const todayStr = noteDateTime ? format(noteDateTime, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

            // Find if there's any note for this medication today
            // existingNotes comes from /v1/nurse-notes which might use 'medication' or 'medicationName'
            // and 'noteDate' or 'dateTime'
            const alreadyGiven = existingNotes.find((n: any) => {
                const nDate = n.dateTime || n.noteDate;
                if (!nDate) return false;

                const noteDate = new Date(nDate);
                const nDateStr = format(noteDate, 'yyyy-MM-dd');

                const nMedName = n.medicationName || n.medication || "";

                return (
                    nDateStr === todayStr &&
                    String(nMedName).toLowerCase() === String(medicationName).toLowerCase()
                );
            });

            if (alreadyGiven) {
                setConfirmationOpen(true);
                return;
            }
        }

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const payload = {
                residentId: String(residentId),
                medicationName,
                dosage,
                dateTime: formatToBackendDateTime(noteDateTime) || "",
                medicationStatus,
                reason,
                schedule,
                result,
                lowStock,
            };

            await clinicalNoteService.createNurseNote(payload);

            // Reset form
            setMedicationName("");
            setDosage("");
            setSchedule("");
            setResult("");
            setLowStock(false);
            setMedicationStatus("");
            setNoteDateTime(new Date());
            setFormErrors({});
            // Refresh existing notes count
            if (residentId) fetchExistingNotes(residentId);

            setSuccess("Progress note saved successfully.");

            if (onSuccess) {
                onSuccess();
            }

            setTimeout(() => setSuccess(null), 1000);
        } catch (e: any) {
            console.error("Failed to save progress note", e);
            setError(
                e?.response?.data?.message || "Failed to save progress note. Please try again."
            );
        } finally {
            setSaving(false);
        }
    };

    const initialResultRef = useRef<string>("");
    const { isListening, startListening, stopListening, isSupported } = useSpeechToText({
        interimResults: true,
        continuous: true,
        onResult: (spokenText) => {
            const base = (initialResultRef.current || "").trim();
            const cleanedSpoken = spokenText.trim();
            const newVal = base
                ? (cleanedSpoken ? `${base} ${cleanedSpoken}` : base)
                : cleanedSpoken;
            setResult(newVal);
        },
        onEnd: () => {
            initialResultRef.current = "";
        }
    });

    return (
        <Paper sx={{ p: 3, ...sx }}>
            <Typography variant="h6" gutterBottom>
                Daily Medication
            </Typography>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
            >
                <DialogTitle>Duplicate Dose Warning</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This medication is scheduled as <strong>Once daily</strong> and the dose has already been administered today.
                        <br /><br />
                        Are you sure you want to exceed the prescribed dose?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmationOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            setConfirmationOpen(false);
                            handleSave(true);
                        }}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        Yes, continue
                    </Button>
                </DialogActions>
            </Dialog>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}
            <Grid container spacing={3} alignItems="flex-start">
                {/* Row 1: Date & Time | Response Status */}
                <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                            label="Date & Time *"
                            format="dd/MM/yyyy hh:mm a"
                            value={noteDateTime}
                            onChange={(v) => {
                                setNoteDateTime(v);
                                if (v) setFormErrors(prev => ({ ...prev, date: undefined }));
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: "medium",
                                    error: !!formErrors.date,
                                    helperText: formErrors.date
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                {/* Medication Field */}
                <Grid item xs={12}>
                    <FormControl fullWidth error={!!formErrors.medication}>
                        <InputLabel>Medication</InputLabel>
                        <Select
                            value={medicationName}
                            label="Medication"
                            onChange={(e) => handleMedicationChange(e.target.value)}
                            disabled={loadingMeds}
                        >
                            <MenuItem disabled value="">
                                <em>Select Medication</em>
                            </MenuItem>
                            {medications.length === 0 && !loadingMeds && (
                                <MenuItem disabled value="">
                                    No active medications found
                                </MenuItem>
                            )}
                            {medications.map((med, index) => (
                                <MenuItem key={med.id || index} value={med.medicationName}>
                                    {med.medicationName}
                                </MenuItem>
                            ))}
                        </Select>
                        {formErrors.medication && (
                            <Typography color="error" variant="caption" sx={{ ml: 1.5, mt: 0.5 }}>
                                {formErrors.medication}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <FormControl fullWidth error={!!formErrors.status}>
                        <InputLabel>Response Status</InputLabel>
                        <Select
                            value={medicationStatus}
                            label="Response Status"
                            onChange={(e) => {
                                setMedicationStatus(e.target.value);
                                if (e.target.value) setFormErrors(prev => ({ ...prev, status: undefined }));
                            }}
                        >
                            <MenuItem value="Taken">Taken</MenuItem>
                            <MenuItem value="NotTaken">Not Taken</MenuItem>
                            <MenuItem value="Missed">Missed</MenuItem>
                            <MenuItem value="Refused">Refused</MenuItem>
                            <MenuItem value="Unknown">Unknown</MenuItem>
                            <MenuItem value="OutOfStock">Out of Stock</MenuItem>
                        </Select>
                        {formErrors.status && (
                            <Typography color="error" variant="caption" sx={{ ml: 1.5, mt: 0.5 }}>
                                {formErrors.status}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Dosage"
                        fullWidth
                        value={dosage}
                        onChange={(e) => {
                            setDosage(e.target.value);
                            if (e.target.value) setFormErrors(prev => ({ ...prev, dosage: undefined }));
                        }}
                        error={!!formErrors.dosage}
                        helperText={formErrors.dosage}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Schedule"
                        fullWidth
                        value={schedule}
                        onChange={(e) => {
                            setSchedule(e.target.value);
                            if (e.target.value) setFormErrors(prev => ({ ...prev, schedule: undefined }));
                        }}
                        error={!!formErrors.schedule}
                        helperText={formErrors.schedule}
                        InputProps={{
                            readOnly: true,
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Reason"
                        fullWidth
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            if (e.target.value) setFormErrors(prev => ({ ...prev, reason: undefined }));
                        }}
                        error={!!formErrors.reason}
                        helperText={formErrors.reason}
                    />
                </Grid>

                {/* Row 3: Results/Response */}
                <Grid item xs={12}>
                    <TextField
                        label="Results/Response"
                        fullWidth
                        multiline
                        minRows={3}
                        value={result}
                        onChange={(e) => setResult(e.target.value)}
                        placeholder="Enter details..."
                        InputProps={{
                            endAdornment: isSupported && (
                                <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                                    <Tooltip title={isListening ? "Stop listening" : "Dictate (Speech to Text)"}>
                                        <IconButton
                                            size="small"
                                            onClick={isListening ? stopListening : () => { initialResultRef.current = result || ""; startListening(); }}
                                            color={isListening ? "error" : "default"}
                                            sx={{
                                                animation: isListening ? "pulse 1.2s infinite" : "none",
                                                "@keyframes pulse": {
                                                    "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.4)" },
                                                    "70%": { boxShadow: "0 0 0 8px rgba(211,47,47,0)" },
                                                    "100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
                                                },
                                            }}
                                        >
                                            {isListening ? <MicOffIcon /> : <MicIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Low Stock Toggle */}
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={lowStock}
                                    onChange={(e) => setLowStock(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Low Stock"
                        />
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        onClick={() => handleSave(false)}
                        disabled={saving}
                        sx={{ backgroundColor: "#0066cc", '&:hover': { backgroundColor: "#00478f" } }}
                    >
                        {saving ? "Saving..." : "Add Daily Medication"}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default DailyProgressNoteForm;